import axios from 'src/utils/axios';

/** *************************************************************************
 * identifyShareCode (CON-1552)
 * Read-only type detection for the universal entry box. The response is
 * { valid, type, summary: { entityName, ownerName }, ownCode,
 *   alreadyApplied, appliedEntityId }.
 *************************************************************************** */
export const identifyShareCode = (
  shareCode,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.post('/api/share-codes/identify', { shareCode });
    doWhenTry(response?.data || { valid: false });
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not check that code. Please try again.');
  }
};

/** *************************************************************************
 * applySharedEntity (CON-1552)
 * ONE generic apply over the entity registry's per-type adapters — the
 * endpoint, request body, and response normalization all come from
 * registry[type].apply, so this creator has zero per-type branching.
 * Group/Event Type failures arrive as HTTP 500 whose response body
 * carries modalTitle/modalBody — the catch path flows
 * error.response.data through the same parseResponse.
 *************************************************************************** */
export const applySharedEntity = (
  entityConfig,
  shareCode,
  doWhenTry = () => { },
  doWhenCatch = () => { },
  // CON-2033: { eventChoices } for keyword, Smart Link and Smart Form codes.
  // Adapters that do not read it ignore it.
  applyOptions = {},
) => async () => {
  const { apply } = entityConfig || {};
  if (!apply?.endpoint) {
    doWhenCatch({ errorBody: 'This share code type cannot be applied here.' });
    return;
  }

  try {
    const response = await axios.post(apply.endpoint, apply.buildBody(shareCode, applyOptions));
    const normalized = apply.parseResponse(response?.data || {});
    // alreadyExists is a successful no-op (keyword name collision) — it has no
    // new id, so admit it alongside the id case.
    if (normalized.success && (normalized.id || normalized.alreadyExists)) {
      doWhenTry({
        ...normalized,
        generatedName: response?.data?.generatedName || null,
      });
    } else {
      doWhenCatch(normalized);
    }
  } catch (e) {
    // Most parseResponse adapters read only modalBody/message, so the
    // maintenance 503's { error: '<string>' } would fall to the generic text.
    const data = e?.response?.data || {};
    const normalized = apply.parseResponse(data);
    const maintenanceMessage = e?.response?.status === 503 && typeof data.error === 'string'
      ? data.error
      : null;
    doWhenCatch({
      ...normalized,
      errorBody: normalized.errorBody || maintenanceMessage,
    });
  }
};

/** *************************************************************************
 * previewShareCodeEventLinks (CON-2033)
 * "Link your events" cards for a single keyword, Smart Link or Smart Form
 * code: POST /api/share-codes/event-links { shareCode } -> { eventLinks }.
 * Plan only, no writes. Empty for any other type, the user's own code, and a
 * keyword the user already has.
 *************************************************************************** */
export const previewShareCodeEventLinks = (
  shareCode,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.post('/api/share-codes/event-links', { shareCode });
    doWhenTry(Array.isArray(response?.data?.eventLinks) ? response.data.eventLinks : []);
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not check this code\'s events. Please try again.');
  }
};

/** *************************************************************************
 * Share Sets — list + create (CON-1552 Slice 4)
 * Redux holds the list + loading (SmartLinks-generation split: list state
 * in Redux, form/modal state local). The backend list endpoint is
 * unpaginated, so there is no query state yet.
 *************************************************************************** */
export const GET_SHARE_SETS = '@shareCenter/get-share-sets';
export const GET_SHARE_SETS_SUCCESS = '@shareCenter/get-share-sets-success';
export const GET_SHARE_SETS_ERROR = '@shareCenter/get-share-sets-error';
export const CLEAR_SHARE_SETS = '@shareCenter/clear-share-sets';

export const getShareSets = () => async (dispatch) => {
  dispatch({ type: GET_SHARE_SETS });
  try {
    const response = await axios.get('/api/share-sets');
    dispatch({
      type: GET_SHARE_SETS_SUCCESS,
      payload: { shareSets: response?.data?.shareSets || [] },
    });
  } catch (e) {
    dispatch({ type: GET_SHARE_SETS_ERROR });
  }
};

export const clearShareSets = () => ({ type: CLEAR_SHARE_SETS });

// Referral onboarding: a 409 means another of the author's Sets is already
// offered to their referrals and nothing was saved. It goes to
// doWhenConflict with that Set ({ id, name }) when the caller passes one, so
// it can show the "Only one set can be offered" window; resending with
// replaceReferralOnboarding: true is "Offer this set instead". Without
// doWhenConflict it reports through doWhenCatch like any other error.
const readReferralConflict = (e) => (e?.response?.status === 409
  ? e?.response?.data?.conflictingShareSet || null
  : null);

export const createShareSet = (
  {
    name, description, isSubscription = false,
    isReferralOnboarding = false, replaceReferralOnboarding = false,
  },
  doWhenTry = () => { },
  doWhenCatch = () => { },
  doWhenConflict = null,
) => async (dispatch) => {
  try {
    const response = await axios.post('/api/share-sets', {
      name,
      description,
      isSubscription,
      isReferralOnboarding,
      replaceReferralOnboarding,
    });
    const shareSet = response?.data?.shareSet;
    if (!shareSet?.id) {
      doWhenCatch(response?.data?.message || 'Could not create the Set.');
      return;
    }
    // Keep the list fresh for back-navigation without an extra fetch.
    dispatch(getShareSets());
    doWhenTry(shareSet);
  } catch (e) {
    const conflict = readReferralConflict(e);
    if (conflict && doWhenConflict) {
      doWhenConflict(conflict);
      return;
    }
    doWhenCatch(e?.response?.data?.message || 'Could not create the Set.');
  }
};

// Share Center tab (Redux-held like SmartFieldsSettingsView's
// currentNavigationTab, so the selection survives detail round-trips).
export const SET_SHARE_CENTER_TAB = '@shareCenter/set-tab';

export const setShareCenterTab = (tab) => ({
  type: SET_SHARE_CENTER_TAB,
  payload: { tab },
});

/** *************************************************************************
 * Share Set detail + items + preview (CON-1552 Slice 5)
 * Callback thunks (detail state is local to the view); the list slice in
 * Redux is refreshed where counts change.
 *************************************************************************** */
export const getShareSet = (
  shareSetId,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.get(`/api/share-sets/${shareSetId}`);
    const shareSet = response?.data?.shareSet;
    if (shareSet?.id) {
      doWhenTry(shareSet);
    } else {
      doWhenCatch('Set not found.');
    }
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not load the Set.');
  }
};

export const updateShareSet = (
  shareSetId,
  {
    name, description, isSubscription, isReferralOnboarding, replaceReferralOnboarding,
  },
  doWhenTry = () => { },
  doWhenCatch = () => { },
  doWhenConflict = null,
) => async () => {
  try {
    // Undefined fields are dropped from the JSON body, and the server leaves
    // an absent field alone, so callers send only what they mean to change.
    const response = await axios.put(`/api/share-sets/${shareSetId}`, {
      name,
      description,
      isSubscription,
      isReferralOnboarding,
      replaceReferralOnboarding,
    });
    const shareSet = response?.data?.shareSet;
    if (shareSet?.id) {
      doWhenTry(shareSet);
    } else {
      doWhenCatch(response?.data?.message || 'Could not save the Set.');
    }
  } catch (e) {
    const conflict = readReferralConflict(e);
    if (conflict && doWhenConflict) {
      doWhenConflict(conflict);
      return;
    }
    doWhenCatch(e?.response?.data?.message || 'Could not save the Set.');
  }
};

/** *************************************************************************
 * publishShareSet (CON-1552 Model C — author publish)
 * Snapshots the Set's current live content as a new published version
 * (snapshot-or-reuse server-side). The detail page enables this only when
 * changes are pending; on success it reloads so the version number + state
 * (and the now-shareable code) refresh.
 *************************************************************************** */
export const publishShareSet = (
  shareSetId,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.post(`/api/share-sets/${shareSetId}/publish`, {});
    if (response?.data?.success) {
      doWhenTry({
        version: response.data.version || null,
        reused: Boolean(response.data.reused),
      });
    } else {
      doWhenCatch(response?.data?.message || 'Could not publish the Set.');
    }
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not publish the Set.');
  }
};

export const addShareSetItems = (
  shareSetId,
  items,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.post(`/api/share-sets/${shareSetId}/items`, { items });
    if (response?.data?.success) {
      doWhenTry({
        added: response.data.added || [],
        skipped: response.data.skipped || [],
      });
    } else {
      doWhenCatch(response?.data?.message || 'Could not add the items.');
    }
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not add the items.');
  }
};

export const removeShareSetItem = (
  shareSetId,
  setItemId,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.delete(`/api/share-sets/${shareSetId}/items/${setItemId}`);
    if (response?.data?.success) {
      doWhenTry();
    } else {
      doWhenCatch(response?.data?.message || 'Could not remove the item.');
    }
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not remove the item.');
  }
};

export const deleteShareSet = (
  shareSetId,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.delete(`/api/share-sets/${shareSetId}`);
    if (response?.data?.success) {
      doWhenTry();
    } else {
      doWhenCatch(response?.data?.message || 'Could not delete the Set.');
    }
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not delete the Set.');
  }
};

export const updateShareSetItemDescription = (
  shareSetId,
  setItemId,
  description,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.put(
      `/api/share-sets/${shareSetId}/items/${setItemId}`,
      { description },
    );
    if (response?.data?.success) {
      doWhenTry(response.data.item || null);
    } else {
      doWhenCatch(response?.data?.message || 'Could not save the description.');
    }
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not save the description.');
  }
};

export const getShareSetPreview = (
  shareSetId,
  doWhenTry = () => { },
  doWhenCatch = () => { },
  // 'source' = the author manifest (what each item carries on the owner's
  // account); default 'target' = recipient semantics for the Chunk 5 picker.
  perspective = 'target',
) => async () => {
  try {
    const response = await axios.get(`/api/share-sets/${shareSetId}/preview`, {
      params: perspective === 'source' ? { perspective } : {},
    });
    if (response?.data?.preview) {
      doWhenTry(response.data.preview);
    } else {
      doWhenCatch(response?.data?.message || 'Could not load the preview.');
    }
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not load the preview.');
  }
};

/** *************************************************************************
 * Recipient Set apply — share-code-first (CON-1552 Chunk 5)
 * The recipient never sees a Set id; both endpoints take the share code in
 * the POST body so codes stay out of URL/access logs. previewSet returns
 * the target-perspective pre-flight (with per-item dependsOnSetItemIds);
 * applySet runs the one-transaction apply for the selected items.
 *************************************************************************** */
export const previewSet = (
  shareCode,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.post('/api/share-codes/sets/preview', { shareCode });
    if (response?.data?.preview) {
      doWhenTry(response.data.preview);
    } else {
      doWhenCatch(response?.data?.message || 'Could not load this Set.');
    }
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not load this Set.');
  }
};

// The error text for a failed Set apply. A 400 body is { error, message }
// where error is often an empty object (an Error serializes to {}), and
// rendering that object crashed the picker. Only a string error (the 503
// maintenance body) is shown as is; otherwise the message.
const readApplyErrorText = (data) => (typeof data?.error === 'string' && data.error)
  || data?.message
  || 'The Set could not be applied. Please try again.';

export const applySet = (
  shareCode,
  selectedSetItemIds,
  // CON-1552 subscription opt-in: undefined when the Set does not offer a
  // subscription (the picker hides the checkbox); the server gates it anyway.
  subscribeToUpdates,
  doWhenTry = () => { },
  doWhenCatch = () => { },
  // "Link your events": one entry per visible event card. Undefined leaves
  // it out of the body (every event reference becomes Needs an event).
  eventChoices = undefined,
) => async () => {
  try {
    const response = await axios.post('/api/share-codes/sets/apply', {
      shareCode,
      selectedSetItemIds,
      subscribeToUpdates,
      eventChoices,
    });
    if (response?.data?.success) {
      doWhenTry(response.data);
    } else {
      doWhenCatch({ errorBody: readApplyErrorText(response?.data) });
    }
  } catch (e) {
    // The maintenance gate returns 503 { error } — surface it verbatim, the
    // same treatment the contact-import flow gives the pause-execution stop.
    doWhenCatch({ errorBody: readApplyErrorText(e?.response?.data) });
  }
};

/** *************************************************************************
 * getPendingShareSetUpdates (CON-1552 Model C — decision B)
 * The PERSISTENT "Sets with updates" banner feed: every Set with a real
 * pending update (applied < latest published), independent of the sign-in
 * modal's dedup stamp. The modal keeps using the login-bootstrap list; this
 * keeps the banner showing actionable work after the modal is dismissed.
 *************************************************************************** */
export const getPendingShareSetUpdates = (
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.get('/api/share-sets/updates/pending');
    doWhenTry(response?.data?.sets || []);
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not load pending updates.');
  }
};

/** *************************************************************************
 * getUpdatePreview — recipient update picker read model (CON-1552 Chunk 6
 * Stage D Part 1). The recipient HAS a subscription, so this is share-set-id
 * based (not share-code): the Stage B diff per item enriched with the per-SA
 * reconcile + cascade-choice facts for Modified Type items. Read-only.
 *************************************************************************** */
export const getUpdatePreview = (
  shareSetId,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.get(`/api/share-sets/${shareSetId}/update-preview`);
    if (response?.data?.items) {
      doWhenTry(response.data);
    } else {
      doWhenCatch(response?.data?.message || 'Could not load this Set’s updates.');
    }
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not load this Set’s updates.');
  }
};

/** *************************************************************************
 * acceptSetUpdates — pull selected updates IN PLACE (CON-1552 Chunk 6 Stage D
 * Part 2). Server re-derives changeType; returns a per-item results array
 * (updated / failed / kept / skipped).
 *************************************************************************** */
export const acceptSetUpdates = (
  shareSetId,
  selectedItems,
  cascadeByType,
  doWhenTry = () => { },
  doWhenCatch = () => { },
  eventChoices = undefined,
) => async () => {
  try {
    // CON-2033: eventChoices (the "Link your events" answers) only when the
    // preview carried eventLinks. Omitting it is legal for the server.
    const response = await axios.post(`/api/share-sets/${shareSetId}/update-accept`, {
      selectedItems,
      cascadeByType,
      ...(Array.isArray(eventChoices) ? { eventChoices } : {}),
    });
    if (response?.data?.results) {
      doWhenTry(response.data);
    } else {
      doWhenCatch(response?.data?.message || 'Could not pull these updates.');
    }
  } catch (e) {
    const { message, error } = e?.response?.data || {};
    // The maintenance 503 carries its text in `error`, not `message`.
    const maintenanceMessage = e?.response?.status === 503 && typeof error === 'string' ? error : null;
    doWhenCatch(message || maintenanceMessage || 'Could not pull these updates.');
  }
};

/** *************************************************************************
 * fetchEntityOptions (CON-1552 Slice 5)
 * Generic picker-source fetch over registry[type].fetchOptions, mirroring
 * applySharedEntity's adapter approach: zero per-type branching here.
 *************************************************************************** */
export const fetchEntityOptions = (
  entityConfig,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  const { fetchOptions } = entityConfig || {};
  if (!fetchOptions?.endpoint) {
    doWhenTry([]);
    return;
  }

  try {
    const response = fetchOptions.method === 'post'
      ? await axios.post(fetchOptions.endpoint, fetchOptions.body || {})
      : await axios.get(fetchOptions.endpoint, { params: fetchOptions.params || {} });
    doWhenTry(fetchOptions.parseList(response?.data || {}));
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || `Could not load your ${entityConfig.pluralLabel}.`);
  }
};

/** *************************************************************************
 * getShareCodeApplyHistory (CON-2002)
 * The account's own share-code applies, newest first:
 * { entries: [{ id, createdAt, shareCode, itemType, itemName, sharedByName,
 *   result }], count }.
 *************************************************************************** */
export const getShareCodeApplyHistory = (
  { page = 1, pageSize = 25 } = {},
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.get('/api/share-codes/history', { params: { page, pageSize } });
    doWhenTry(response?.data || { entries: [], count: 0 });
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not load your history. Please try again.');
  }
};

/** *************************************************************************
 * getShareCodeApplyHistoryEntry (CON-2002)
 * One entry's snapshot: { created, skipped, target }, each item with
 * available (still exists in the account).
 *************************************************************************** */
export const getShareCodeApplyHistoryEntry = (
  id,
  doWhenTry = () => { },
  doWhenCatch = () => { },
) => async () => {
  try {
    const response = await axios.get(`/api/share-codes/history/${id}`);
    doWhenTry(response?.data?.entry || null);
  } catch (e) {
    doWhenCatch(e?.response?.data?.message || 'Could not load this entry. Please try again.');
  }
};
